import React, { useState, useEffect, useCallback, useContext, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import i18n from 'i18n-js';
import moment from 'moment';
import Can from 'components/Can';
import { useQuery } from 'hooks';
import { Table } from 'components/UI';
import useStyles from './List.styled';
import { UserContext } from 'context';
import { useHistory } from 'react-router-dom';
import { buildUrl, toQueryString } from 'utils';
import OrgAbility from 'components/OrgAbility';
import SearchIcon from '@material-ui/icons/Search';
import { SearchField } from 'components/FormFields';
import { UsersSelect } from 'components/AsyncFields';
import { ReservationModal } from 'components/Modals';
import { VehiclesSelect } from 'components/AsyncFields';
import { DateTimePickerField } from 'components/FormFields';
import InputAdornment from '@material-ui/core/InputAdornment';
import { VehiclesGroupsSelect } from 'components/AsyncFields';
import { ReservationStatusSelect } from 'components/DataFields';
import { ReservationsActions } from 'state/modules/reservations';
import {
  Box,
  Grid,
  Paper,
  Switch,
  Button,
  Container,
  Typography,
  FormControlLabel,
} from '@material-ui/core';
import { PartnersSelect } from 'components/AsyncFields';
import { debounce } from 'lodash';
import { VehicleGroupsTimezonesSelect } from 'components/AsyncFields';
import { RadioButtonsGroup, RadioButton } from 'components/FormFields';

const DATE_FILTER_MODE = {
  RANGE: 'range',
  ONLY: 'only',
};

const ReservationsList = () => {
  const classes = useStyles();
  const urlParams = useQuery();
  const dispatch = useDispatch();
  const history = useHistory();
  const org = useSelector((state) => state.auth?.organizations?.current) || {};
  const loadingReservations = useSelector((state) => state.reservations?.loading);
  const reservations = useSelector((state) => state.reservations?.reservations);
  const pagination = useSelector((state) => state.reservations?.pagination);
  const fetchedReservationSuccess = useSelector(
    (state) => state.reservations?.fetchedReservationSuccess
  );
  const core = useSelector((state) => state.config?.settings?.core || {});
  const hasPartnership = core?.partnership?.enabled || false;

  const [openReservationModal, setOpenReservationModal] = useState(false);
  const sharingType = useSelector(
    (state) => state.config?.settings?.core?.partnership?.sharing_type
  );
  const { localeFormat } = useSelector((state) => state.organizations);
  const { orgType } = useContext(UserContext);
  const isFFBEnabled = sharingType === 'FFB' && hasPartnership;
  const isOwner = orgType === 'msOwnerOrg';
  const isService = orgType === 'msServiceOrg';
  const [params, setParams] = useState({
    page: 1,
    limit: 10,
    date_filter: DATE_FILTER_MODE.RANGE,
    time_zone: moment.tz.guess(),
    ...urlParams,
  });
  const isMounted = useRef(false);
  const abortController = useRef(null);

  const loading = loadingReservations || org?.id === undefined;

  const createReservation = core?.reservation?.create;
  const employeeReservationAllowed = core?.reservation?.enabled && createReservation;
  const omitEmployeeColumn = isOwner && isFFBEnabled;

  const columns = [
    {
      name: i18n.t('tableColumns.id'),
      selector: 'id',
      sortable: true,
      grow: 1,
      maxWidth: '90px',
      minWidth: '70px',
    },
    {
      name: i18n.t('common.partner'),
      selector: (row) => (row?.organization?.name !== org?.name && row?.organization?.name) || '--',
      grow: 1,
      minWidth: '110px',
      omit: !omitEmployeeColumn,
    },
    {
      name: orgType === 'msOwnerOrg' ? i18n.t('common.employee') : i18n.t('tableColumns.customer'),
      cell: (row) =>
        row?.user?.first_name || row?.user?.last_name
          ? (row?.user?.first_name || '') + ' ' + (row?.user?.last_name || '')
          : '--',
      grow: 1,
      omit: !((isOwner && createReservation) || isService),
    },
    {
      name: i18n.t('tableColumns.start'),
      selector: 'used_times',
      sortable: true,
      minWidth: '165px',
      cell: (row) =>
        row?.start_reserved ? (
          <div>
            <DatetimeField datetime={row?.start_reserved} tz={row?.vehicle_group?.time_zone} />
            {row?.start_used && (
              <div className={classes?.time}>
                <DatetimeField datetime={row?.start_used} tz={row?.vehicle_group?.time_zone} />
              </div>
            )}
          </div>
        ) : (
          '--'
        ),
      grow: 1,
    },
    {
      name: i18n.t('tableColumns.end'),
      minWidth: '165px',
      cell: (row) =>
        row?.end_reserved ? (
          <div>
            <DatetimeField datetime={row?.end_reserved} tz={row?.vehicle_group?.time_zone} />
            {row?.end_used && (
              <div className={classes?.time}>
                <DatetimeField datetime={row?.end_used} tz={row?.vehicle_group?.time_zone} />
              </div>
            )}
          </div>
        ) : (
          '--'
        ),
      grow: 1,
    },
    {
      name: i18n.t('tableColumns.vehicle_group'),
      cell: (row) => row?.vehicle_group?.name || '--',
      grow: 2,
      wrap: true,
      omit: hasPartnership,
    },
    {
      name: i18n.t('tableColumns.vehicle'),
      cell: (row) => row?.vehicle?.name || '--',
      grow: 1,
      wrap: true,
    },
    {
      name: i18n.t('tableColumns.location'),
      cell: (row) => row?.start_reserved_location?.name || '--',
      grow: 1,
      wrap: true,
      minWidth: '120px',
    },
    {
      name: i18n.t('common.fleet'),
      selector: (row) =>
        row?.vehicle_group?.parent?.name ||
        (row?.organization?.name === org?.name && row?.vehicle_group?.name) ||
        '--',
      grow: 2,
      wrap: true,
    },
    {
      name: i18n.t('common.fleet_owner'),
      cell: (row) => row?.vehicle_group?.vehicle_owner?.name || '--',
      grow: 2,
      wrap: true,
      omit: orgType !== 'msServiceOrg',
    },
    {
      name: i18n.t('tableColumns.service'),
      cell: (row) => row?.organization?.name || '--',
      grow: 1,
      minWidth: '110px',
      wrap: true,
      omit: orgType !== 'msOwnerOrg' || omitEmployeeColumn,
    },
    {
      name: i18n.t('tableColumns.status'),
      grow: 1,
      minWidth: '120px',
      cell: (row) =>
        row?.status ? (
          <div className={`${classes.status} ${classes[row?.status.toLowerCase()]}`}>
            <span className="status">
              {i18n.t(`reservations_statuses.${row?.status}`, {
                defaultValue: row?.status,
              })}
            </span>
          </div>
        ) : (
          '--'
        ),
    },
  ];

  const mapParamsToQuery = (state) => {
    const query = { ...state };
    // Remove organization_id if false by param/switch or url
    if (['false', false].includes(query.organization_id)) {
      delete query.organization_id;
    }
    if (query.start_reserved) {
      query.start_reserved = moment(query.start_reserved).tz(query.time_zone, true).format();
    } else {
      delete query.start_reserved;
    }
    if (query.end_reserved) {
      query.end_reserved = moment(query.end_reserved).tz(query.time_zone, true).format();
    } else {
      delete query.end_reserved;
    }
    if (query.date_filter === DATE_FILTER_MODE.ONLY) {
      if (query.start_reserved) {
        query.start_reserved = moment(query.start_reserved).startOf('day').format();
        query.start_reserved_upper = moment(query.start_reserved).endOf('day').format();
      }
      if (query.end_reserved) {
        query.end_reserved = moment(query.end_reserved).startOf('day').format();
        query.end_reserved_upper = moment(query.end_reserved).endOf('day').format();
      }
    }
    delete query.time_zone;
    delete query.date_filter;
    return query;
  };

  const fetchReservations = useCallback(
    (params = {}) => {
      if (params?.organization_id && params?.organization_id !== org.id) {
        params.vehicle_owner_id = org.id;
      } else if (params?.vehicle_owner_id && !org.id && params?.vehicle_owner_id !== org.id) {
        delete params.vehicle_owner_id;
      }
      // Abort previous if any request
      abortController?.current?.abort();
      // Create new abort controller and send signal
      abortController.current = new AbortController();
      dispatch(
        ReservationsActions.fetchReservations(mapParamsToQuery(params), {
          signal: abortController?.current?.signal,
        })
      );
    },
    [dispatch, org.id]
  );

  const fetchReservationsDebounced = useCallback(
    debounce((params) => fetchReservations(params), 1000),
    [fetchReservations]
  );

  useEffect(() => {
    dispatch(ReservationsActions.resetUpdateReservation());
  }, [dispatch]);

  // On params change once is mounted, debounced
  useEffect(() => {
    if (isMounted.current) {
      fetchReservationsDebounced(params);
    } else {
      if (org.id) {
        isMounted.current = true;
        fetchReservations(params);
      }
    }
  }, [fetchReservationsDebounced, params]);

  // On outdated data
  useEffect(() => {
    if (fetchedReservationSuccess) {
      fetchReservations(params);
    }
  }, [fetchReservations, params, fetchedReservationSuccess]);

  /**
   * Params handler that updates the state and set new query string.
   * @param {*} newParams parameters to set state, we do merge/resolution before calling the handler
   */
  const handleParams = (newParams) => {
    setParams(newParams);
    const qs = toQueryString(newParams);
    history.replace(buildUrl(`/reservations${qs}`));
  };

  const handlePageChange = (page) => {
    const newParams = { ...params, page: page, limit: params.limit };
    handleParams(newParams);
  };

  // sort filter
  const handleSort = ({ column, sortDirection }) => {
    const newParams = {
      ...params,
      sort_field: column.selector,
      sort_desc: sortDirection === 'desc' ? true : false,
    };
    handleParams(newParams);
  };

  // change number of items per page
  const handlePerRowsChange = async (limit) => {
    const newParams = { ...params, page: 1, limit: limit };
    handleParams(newParams);
  };

  /**
   * Handle row click
   * @param {object} row Data of the row selected
   */
  const handleRowClick = (row) => {
    if (row.user) history.push(buildUrl(`/reservations/${row?.id}`));
  };
  // search by status, keyword and organization
  const search = (name, value) => {
    const filters = {
      ...params,
      page: 1,
      // validate if value is an object
      [name]: value,
    };

    delete filters.all_services;

    if (name === 'all_services' && value) {
      filters.organization_id = false;
    } else if (name === 'all_services' && !value) {
      filters.organization_id = org?.id;
    }
    if (name === 'organization_id' && value !== org.id && value) {
      filters.vehicle_owner_id = org.id;
    } else if (name === 'organization_id' && value === '') {
      filters.vehicle_owner_id = undefined;
    }
    // remove empty fields
    Object.keys(filters).forEach(
      (key) => !filters[key] && filters[key] !== false && delete filters[key]
    );
    handleParams(filters);
  };

  const DatetimeField = ({ datetime, tz = moment.tz.guess() }) => {
    return (
      <span>
        <p>{moment(datetime).tz(tz).format(`${localeFormat} hh:mm A`)}</p>
        <p className={classes.timezone}>{moment().tz(tz).format('UTC Z')}</p>
      </span>
    );
  };

  const handleOpenReservationModal = () => {
    setOpenReservationModal((prevState) => !prevState);
  };

  return (
    <Container className={classes.container}>
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <Grid
            container
            spacing={2}
            direction="row"
            alignItems="center"
            justifyContent="space-between"
          >
            <Grid item>
              <Typography variant="h6" gutterBottom>
                <Box fontWeight="fontWeightBold" data-cy="title" fontSize={24} my={1}>
                  {i18n.t('reservations.title')}
                </Box>
              </Typography>
            </Grid>
            {createReservation ? (
              <Can I="create" a="reservation">
                <Grid item>
                  <Button
                    color="primary"
                    variant="contained"
                    data-cy="create-reservation-button"
                    onClick={handleOpenReservationModal}
                    style={{ textTransform: 'capitalize' }}
                  >
                    {i18n.t('reservations.create')}
                  </Button>
                </Grid>
              </Can>
            ) : null}
          </Grid>
        </Grid>
        <Grid item xs={12} style={{ paddingBottom: '25px' }}>
          <Grid
            container
            spacing={2}
            direction="row"
            justifyContent="flex-start"
            alignItems="flex-start"
          >
            <Grid item style={{ width: '60%' }}>
              <Grid
                container
                spacing={2}
                direction="row"
                justifyContent="flex-start"
                alignItems="flex-end"
              >
                <Grid item xs={4}>
                  <SearchField
                    data-cy="reservation-id-filter"
                    margin="none"
                    label={i18n.t('reservations.search_reservation')}
                    onKeyPress={(e) => !/[0-9]/.test(e.key) && e.preventDefault()}
                    field={{
                      onChange: search,
                      name: 'reservation_id',
                      value: params.reservation_id,
                    }}
                    inputProps={{
                      startAdornment: (
                        <InputAdornment
                          classes={{
                            root: classes.searchIcon,
                          }}
                          position="start"
                        >
                          <SearchIcon />
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>
                {((orgType === 'msOwnerOrg' && employeeReservationAllowed) ||
                  orgType !== 'msOwnerOrg') && (
                  <Grid item xs={4}>
                    <UsersSelect
                      form={{
                        setFieldValue: search,
                      }}
                      type="users"
                      margin="none"
                      field={{
                        name: 'user_id',
                        value: params.user_id || '',
                      }}
                      datacy="customer-filter"
                      customParams={{ keyword: params.user_id }}
                      label={
                        orgType === 'msOwnerOrg'
                          ? i18n.t('common.employee')
                          : i18n.t('common.filter_by_customer')
                      }
                    />
                  </Grid>
                )}
                <Grid item xs={4}>
                  <VehiclesSelect
                    form={{
                      setFieldValue: search,
                    }}
                    field={{
                      name: 'vehicle_id',
                      value: params.vehicle_id || '',
                    }}
                    label={i18n.t('common.vehicle')}
                    datacy="vehicle-filter"
                    customParams={{ keyword: params.vehicle_id }}
                  />
                </Grid>
                {orgType === 'msOwnerOrg' ? null : (
                  <Grid item xs={4}>
                    <VehiclesGroupsSelect
                      form={{
                        setFieldValue: search,
                      }}
                      margin="none"
                      field={{
                        name: 'group_id',
                        value: params.group_id || '',
                      }}
                      label={i18n.t('common.vehicle_group')}
                    />
                  </Grid>
                )}
                <Grid item xs={4}>
                  <ReservationStatusSelect
                    form={{
                      setFieldValue: search,
                    }}
                    margin="none"
                    field={{
                      name: 'statuses',
                      value: params.statuses || '',
                    }}
                    label={i18n.t('common.status')}
                  />
                </Grid>
                {isService || (isOwner && isFFBEnabled) ? (
                  <Grid item xs={4}>
                    <PartnersSelect
                      form={{ setFieldValue: search }}
                      label={
                        isOwner
                          ? i18n.t('vehicle.filter_by_partner')
                          : i18n.t('common.by_fleet_owner')
                      }
                      field={{
                        name: isOwner && isFFBEnabled ? 'organization_id' : 'vehicle_owner_id',
                        value:
                          isOwner && isFFBEnabled
                            ? params?.organization_id
                            : params?.vehicle_owner_id,
                      }}
                      margin="none"
                    />
                  </Grid>
                ) : null}
                {!hasPartnership && (
                  <Grid item xs={4}>
                    <FormControlLabel
                      value="start"
                      className={classes.switchLabel}
                      control={
                        <Switch
                          onChange={(_, value) => search('all_services', value)}
                          checked={params.organization_id !== org?.id}
                          name="active"
                          color="primary"
                        />
                      }
                      label={i18n.t('reservations.include_all_services')}
                      labelPlacement="start"
                    />
                  </Grid>
                )}
                <Grid item xs={4}>
                  <VehiclesGroupsSelect
                    form={{ setFieldValue: search }}
                    label={i18n.t('common.by_fleet')}
                    field={{
                      name: 'vehicle_group_parent_id',
                      value: params?.vehicle_group_parent_id,
                    }}
                    margin="none"
                    type="vgList"
                    customParams={{ group_type: 'OWNER' }}
                  />
                </Grid>
                <Grid item xs={4}>
                  <VehicleGroupsTimezonesSelect
                    margin="none"
                    form={{
                      setFieldValue: (field, value) => value && search('time_zone', value),
                    }}
                    field={{
                      name: 'time_zone',
                      value: params?.time_zone,
                    }}
                    label={i18n.t('dashboard.time_zone')}
                  />
                </Grid>
              </Grid>
            </Grid>
            <Grid item style={{ width: '40%', alignSelf: 'baseline' }}>
              <Paper elevation={2} className={classes.paper}>
                <Grid
                  container
                  spacing={2}
                  direction="row"
                  justifyContent="flex-start"
                  alignItems="flex-end"
                  className={classes.dateFilters}
                  style={{ padding: '0 1rem' }}
                >
                  <Grid item xs={6}>
                    <DateTimePickerField
                      form={{
                        setFieldValue: search,
                      }}
                      field={{
                        name: 'start_reserved',
                        value: params.start_reserved,
                      }}
                      isClearable={true}
                      editable={true}
                      label={i18n.t('common.start_reserved')}
                      showTimeSelect={params.date_filter === DATE_FILTER_MODE.RANGE}
                      dateFormat={
                        params.date_filter === DATE_FILTER_MODE.ONLY ? 'MMMM d, yyyy' : undefined
                      }
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <DateTimePickerField
                      form={{
                        setFieldValue: search,
                      }}
                      isClearable={true}
                      field={{
                        name: 'end_reserved',
                        value: params.end_reserved,
                      }}
                      editable={true}
                      label={i18n.t('common.end_reserved')}
                      showTimeSelect={params.date_filter === DATE_FILTER_MODE.RANGE}
                      dateFormat={
                        params.date_filter === DATE_FILTER_MODE.ONLY ? 'MMMM d, yyyy' : undefined
                      }
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <RadioButtonsGroup
                      name="date_filter"
                      value={params.date_filter}
                      // disabled={!values.vehicle_group_id}
                      setFieldValue={search}
                      nonBool
                      row
                      label={
                        <span style={{ fontSize: '0.75rem' }}>{i18n.t('common.show_only')}</span>
                      }
                    >
                      <RadioButton
                        value={DATE_FILTER_MODE.RANGE}
                        label={i18n.t(`common.date_label.${DATE_FILTER_MODE.RANGE}`)}
                      />
                      <RadioButton
                        value={DATE_FILTER_MODE.ONLY}
                        label={i18n.t(`common.date_label.${DATE_FILTER_MODE.ONLY}`)}
                      />
                    </RadioButtonsGroup>
                  </Grid>
                  <div style={{ width: 1, height: '3rem' }} />
                </Grid>
              </Paper>
            </Grid>
          </Grid>
        </Grid>
        <Grid item xs={12}>
          <Paper elevation={2} className={classes.paper}>
            <Table
              columns={columns}
              loading={loading}
              page={params.page}
              data={reservations}
              onSort={handleSort}
              limit={params.limit}
              handleRowClicked={handleRowClick}
              handlePageChange={handlePageChange}
              defaultSortAsc={!params?.sort_desc}
              defaultSortField={params?.sort_field}
              totalRows={pagination?.totalRows || 0}
              handlePerRowsChange={handlePerRowsChange}
            />
          </Paper>
        </Grid>
      </Grid>
      {openReservationModal && (
        <ReservationModal open={openReservationModal} handleClick={handleOpenReservationModal} />
      )}
    </Container>
  );
};

export default ReservationsList;
